import React from 'react';
import Button from "components/Button";
import {ExclamationCircleIcon, PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Field from "components/Form/Field";
import FieldHeader from "components/Form/FieldHeader";
import Well from "components/Well";
import {FieldArrayRenderProps, useField} from "formik";
import {LevelData} from "types/Level";
import {Feature, FeatureCollection, Polygon} from "geojson";
import {MapEditor} from "../../../components/MapEditor";
import * as Mapbox from "react-map-gl/mapbox";
import {Source} from "react-map-gl/mapbox";
import {featureCollection} from "../../../services/geometry";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {TextAreaField} from "../../../components/Form/Form";
import {getViewState} from "../../../services/map";

// Helpers ----

function editableLevel(level?: LevelData): FeatureCollection | null{
  return level?.area? {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {},
        geometry: level.area
      }
    ]
  } : null
}

// Component ----

type LevelFormProps = {
  siteArea?: Polygon
  level: LevelData
  index: number
  helpers: FieldArrayRenderProps
  name?: string
}

function LevelForm({siteArea, level, index, helpers, name = 'levels'}: LevelFormProps) {
  const [, meta] = useField(name);

  const currentSite = featureCollection(siteArea? [
    {type: "Feature", properties: {type: 'site'}, geometry: siteArea}
  ] : []);

  function handleFeaturesChanged(features: Array<Feature>) {
    const area = features.find(feature => feature.geometry.type === 'Polygon')?.geometry ?? null;

    void helpers.form.setFieldValue(`${name}.${index}.area`, area);
  }

  return (
    <Well
      heading={level.name || `Level ${level.number}`}
      action={
        <Button
          color={Button.colors.WHITE}
          size={Button.sizes.X_SMALL}
          icon={TrashIcon}
          onClick={() => helpers.remove(index)}
        />
      }>

      <div className="grid grid-cols-1 gap-y-6 gap-x-4 lg:grid-cols-6">

        {/* Number */}
        <div className="sm:col-span-2">
          <Field
            label="Number *"
            hint='1 - 255'
            type="number"
            name={`${name}.${index}.number`}
          />
        </div>

        {/* Name */}
        <div className="sm:col-span-4 xl:col-span-3">
          <Field
            label="Name *"
            hint='250 chars max'
            type="text"
            name={`${name}.${index}.name`}
            maxLength={250}
          />
        </div>

        {/* Description */}
        <div className="sm:col-span-6">
          <TextAreaField
            label="Description"
            hint="Optional. 20,000 chars max."
            rows={4}
            name={`${name}.${index}.description`}
            maxChars={20000}
          />
        </div>

        {/* Information */}
        <div className="sm:col-span-6">
          <FieldHeader label="Area *"/>
          <div className="mt-1">
            <MapEditor
              height="32rem"
              tools={{polygon: true}}
              features={editableLevel(level)}
              onFeaturesChanged={handleFeaturesChanged}
              singleFeature
              initialViewState={getViewState(currentSite)}
            >
              <Source type="geojson" id="current-site-area" data={currentSite}>
                <Mapbox.Layer
                  type="fill"
                  id="ngage-current-site"
                  paint={{
                    "fill-color": "#9a9a9a",
                    "fill-opacity": 0.3,
                  }}
                />

                <Mapbox.Layer
                  type="line"
                  id="ngage-current-site-outline"
                  paint={{
                    "line-color": "#949494",
                    "line-width": 3,
                  }}
                />
              </Source>
            </MapEditor>

            <p className="mt-2 text-sm text-gray-500 flex items-center">
              <PencilIcon className="text-gray-600 h-4 w-4 mr-1"/> Draw the level area on the map using the drawing tool
            </p>

            {/* @ts-ignore */}
            {(!!meta.error?.[0]?.area?.coordinates && meta.touched) &&
              <FieldHelp isError>
                <div className="flex items-center gap-2">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
                  {/* @ts-ignore */}
                  {meta.error[0].area.coordinates}
                </div>
              </FieldHelp>
            }
          </div>
        </div>

      </div>
    </Well>
  );
}

export default LevelForm;